import { createUseStyles } from 'react-jss';
import { PRIMARY_STATUS_COLORS, SECONDARY_STATUS_COLORS, StatusVariant } from '@tcl-argon-utils/utils';
import { Classes } from 'jss';
import React from 'react';
import { ActionPalette, BrandPalette, GrayscalePalette } from '@tcl-boron-colors/colors';

const icon = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
};

interface InputStylesData {
  status: StatusVariant;
  leftIcon: React.ReactNode;
  rightIcon: React.ReactNode;
  isPrimary: boolean;
  clearable: boolean;
}
export const useInputStyles: (
  data: InputStylesData & {
    theme: Jss.Theme;
  },
) => Classes<'disabled' | 'leftIcon' | 'rightIcon' | 'root' | 'inputWrapper' | 'input' | 'forceFocus' | 'clearable'> =
  createUseStyles({
    root: { minWidth: '50px' },
    inputWrapper: ({ status, isPrimary }: InputStylesData) => ({
      position: 'relative',
      width: '100%',
      height: '40px',
      border: '1px solid',
      borderRadius: isPrimary ? '20px' : '3px',
      backgroundColor: SECONDARY_STATUS_COLORS[status],
      borderColor:
        status !== 'default' ? PRIMARY_STATUS_COLORS[status] : isPrimary ? 'transparent' : ActionPalette[400],
      transition: 'background-color 0.3s, border 0.3s',
      boxShadow: isPrimary && status === 'default' ? `0px 1px 3px 1px ${GrayscalePalette[300]}` : 'none',
      '&:hover': {
        borderColor: ActionPalette[500],
        backgroundColor: SECONDARY_STATUS_COLORS[status],
      },
      '&:focus-within, &:active': {
        borderColor: ActionPalette[500],
        backgroundColor: status === 'default' ? GrayscalePalette[200] : SECONDARY_STATUS_COLORS[status],
      },
    }),
    rightIconClearable: {
      ...icon,
      right: '12px',
      alignItems: 'center',
    },
    divider: {
      background: 'none',
      border: 'none',
      borderRight: `1px solid ${ActionPalette[400]}`,
      height: '21px',
      boxSizing: 'border-box',
    },
    leftIcon: {
      ...icon,
      left: '12px',
    },
    rightIcon: {
      ...icon,
      right: '12px',
    },
    clearable: {
      '&>div': {
        border: '3px solid transparent',
        borderRadius: '50%',
        height: '30px',
        width: '30px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        outline: 'none',
        '&:focus-visible': {
          border: `3px solid ${ActionPalette[300]}`,
        },
      },
    },
    input: ({ leftIcon, rightIcon, clearable }: InputStylesData) => {
      let padding = '12px 16px';
      const paddingTop = '12px';
      const paddingBottom = '12px';
      const paddingLeft = '16px';
      const paddingRight = '16px';

      if (leftIcon && rightIcon) {
        padding = [paddingTop, '50px'].join(' ');
      } else if (leftIcon) {
        padding = [paddingTop, paddingRight, paddingBottom, '50px'].join(' ');
      } else if (rightIcon || clearable) {
        padding = [paddingTop, '50px', paddingBottom, paddingLeft].join(' ');
      }

      return {
        backgroundColor: 'transparent',
        boxSizing: 'border-box',
        fontSize: '14px',
        fontWeight: '400',
        lineHeight: '20px',
        color: BrandPalette[700],
        height: '100%',
        outline: 'none',
        border: 'none',
        padding,
        resize: 'none',
        width: '100%',
        '&::placeholder': {
          fontSize: '12px',
          fontWeight: '400',
          lineHeight: '16px',
          color: GrayscalePalette[500],
        },
      };
    },
    forceFocus: ({ status }) => ({
      borderColor: ActionPalette[500],
      backgroundColor: status === 'default' ? GrayscalePalette[200] : SECONDARY_STATUS_COLORS[status],
    }),
    disabled: {
      opacity: 0.5,
      pointerEvents: 'none',
    },
  });
